import { CalendarPlus, Clock, RefreshDouble, UserPlus } from 'iconoir-react'
import { ReactElement } from 'react'
import { ActivityCard } from './ActivityCard'
import { KeyValueDrawer } from './KeyValueDrawer'
import { JOURNEY_MILESTONE_DESCRIPTIONS } from 'app/lib/journeyMilestones'
import type { ActivityJson } from 'app/types/entities'
import { assertGood } from 'lib/assertBoom'

type Props = {
  activity: ActivityJson
  isFirst?: boolean
  isLast?: boolean
  alwaysOpen?: boolean
}

type JourneyDescription = (typeof JOURNEY_MILESTONE_DESCRIPTIONS)[number]

const CARD_STYLE: Record<JourneyDescription, { Icon: typeof UserPlus; tileClass: string }> = {
  'Became Lead': { Icon: UserPlus, tileClass: `bg-blue-600` },
  'First Appointment Scheduled': { Icon: CalendarPlus, tileClass: `bg-indigo-600` },
  'Went Inactive': { Icon: Clock, tileClass: `bg-slate-500` },
  Reactivated: { Icon: RefreshDouble, tileClass: `bg-teal-600` },
}

/** Renders the four journey beats projected by the backend timeline projection
 *  (docs/superpowers/specs/2026-08-24-milestone-workflow-design.md). Closed Won
 *  and Closed Lost keep their dedicated cards. */
export function JourneyMilestoneCard({ activity, isFirst, isLast, alwaysOpen }: Props): ReactElement {
  const style = CARD_STYLE[activity.description as JourneyDescription]
  assertGood(activity.type === `Milestone` && style, `Not a journey milestone`)
  const { Icon, tileClass } = style
  return (
    <ActivityCard
      icon={<Icon className={`h-5 w-5 rounded-md p-1 text-on-color ${tileClass}`} />}
      title={
        <div className="flex min-w-0 items-center gap-x-1 truncate text-ui-sm text-strong">{activity.description}</div>
      }
      activityDate={activity?.activityDate}
      drawer={
        <div className="flex w-full items-center rounded-lg border bg-surface px-4 py-3 text-body-xs shadow-sm">
          <KeyValueDrawer map={{ Source: activity.source || `-` }} />
        </div>
      }
      isFirst={isFirst}
      isLast={isLast}
      alwaysOpen={alwaysOpen}
    />
  )
}
